import React from "react";
import { QuoteInstallmentsResponse } from "../../../Common/Types";
import { FormatCurrency } from "../../../Common/Helpers/MoneyHelper";
import { formatDate } from "../../../Common/Helpers/DateHelper";
import NoTranslate from "../../BaseComponents/NoTranslate";
import { LabelKey } from "../../../Common/StoryblokTypes";
import { StringIndexableDictionary } from "../../../Common/Types";
import { Loading } from "@ilc-technology/luik";

type InstallmentsDetailsProps = {
  quoteInstallments: QuoteInstallmentsResponse | undefined;
  loading: boolean;
  language: string;
  labels: StringIndexableDictionary<string>;
  localCurrencyDisclaimer?: string;
};

export const InstallmentsDetails: React.FC<InstallmentsDetailsProps> = ({
  quoteInstallments,
  loading,
  language,
  labels,
  localCurrencyDisclaimer,
}) => {
  const getInstallmentLabel = (index: number, numberOfInstallments: number) => {
    const label = labels[LabelKey.installmentLabel];
    const number = index + 1;
    if (index === 0) {
      return `${label} ${number} (${labels[LabelKey.installmentLabelFirst]})`;
    }
    if (index === numberOfInstallments - 1) {
      return `${label} ${number} (${labels[LabelKey.installmentLabelLast]})`;
    }
    return `${label} ${number}`;
  };

  if (loading) {
    return (
      <div className="bg-gray-5 flex w-full items-center justify-center py-6 pr-3" data-testid="installment-schedule">
        <Loading size="sm" intent="black" />
      </div>
    );
  }

  if (!quoteInstallments?.installments?.items?.length) {
    return null;
  }

  const { items: installmentItems, currencyCode } = quoteInstallments.installments;
  const numberOfInstallments = installmentItems.length;

  return (
    <div className="bg-gray-5 w-full pr-3" data-testid="installment-schedule">
      <div className="mb-2">
        <hr id="installment-schedule-divider" className="h-px w-full border-0 bg-inactive" aria-hidden />
      </div>
      <div className="space-y-2">
        {installmentItems.map((item, index) => (
          <div
            key={index}
            className="grid grid-cols-[1fr_1fr_auto] items-baseline gap-4"
            data-testid={`installment-schedule-item-${index}`}
          >
            <div className="whitespace-nowrap text-xs font-medium text-gray-45">
              {getInstallmentLabel(index, numberOfInstallments)}
            </div>
            <div className="whitespace-nowrap text-right text-xs font-medium text-gray-45">
              <NoTranslate>{formatDate(item.dueDate, language, true)}</NoTranslate>
            </div>
            <div className="whitespace-nowrap text-right text-xs font-bold text-gray-45">
              {FormatCurrency(item.amount, currencyCode, 2)}
            </div>
          </div>
        ))}
      </div>
      {localCurrencyDisclaimer && (
        <div className="mt-2" data-testid="installment-schedule-local-currency-disclaimer">
          <hr className="mb-2 h-px w-full border-0 bg-inactive" aria-hidden />
          <div className="text-xs text-gray-45">{localCurrencyDisclaimer}</div>
        </div>
      )}
    </div>
  );
};
